// utils/branding.ts
//
// Single source of truth for the product name and logo shown in the UI.
//
// thai-production build: this deployment only serves Thailand, so the brand is
// always HealJai regardless of host or the user's region.

export const HEALJAI_NAME = "HealJai";
export const ASTRIA_NAME = "Astria";

export const HEALJAI_LOGO = "/healjai-chat-logo.png";
export const ASTRIA_LOGO = "/astria_logo.png";


export const HEALJAI_SLUG = "healjai";
export const ASTRIA_SLUG = "astria";

// Public-facing site shown in exported/shared artwork.
export const HEALJAI_SITE = "HealJai.com";
export const ASTRIA_SITE = "Astria.com";

// Thailand's region code is "healjai"; "thailand" is accepted as an alias in
// case the backend ever starts sending the human-readable form.
const THAILAND_ORIGINS = ["healjai", "thailand"];

/**
 * thai-production build: this deployment only ever serves Thailand, whatever
 * host it is on, so it is always the HealJai domain and never the Astria one.
 */
export const isHealJaiDomain = (): boolean => true;

/** thai-production build: never the Astria domain (not even on localhost). */
export const isAstriaDomain = (): boolean => false;

/** True when the given origin/region is Thailand. */
export const isThailandOrigin = (origin?: string | null): boolean =>
  THAILAND_ORIGINS.includes((origin || "").trim().toLowerCase());

/**
 * True when the HealJai brand should be shown (always, in this build).
 */
export const isHealJaiBrand = (_origin?: string | null): boolean => true;

/** Product name to display: "HealJai" for Thailand/healjaispace.com, else "Astria". */
export const getBrandName = (origin?: string | null): string =>
  isHealJaiBrand(origin) ? HEALJAI_NAME : ASTRIA_NAME;

/** Logo path to display, matching getBrandName. */
export const getBrandLogo = (origin?: string | null): string =>
  isHealJaiBrand(origin) ? HEALJAI_LOGO : ASTRIA_LOGO;

/** Alt text for the brand logo, e.g. "HealJai logo". */
export const getBrandLogoAlt = (origin?: string | null): string =>
  `${getBrandName(origin)} logo`;

/**
 * Swap any HealJai spelling in backend/AI-authored copy for the active brand.
 * Covers the casing variants that exist in the codebase (HealJai, Healjai,
 * HeaLJai) and the Thai script form used in th.json.
 */
export const applyBrandName = (
  text: string,
  origin?: string | null,
): string => {
  if (!text || isHealJaiBrand(origin)) return text;
  return text
    .replace(/heal\s*jai/gi, ASTRIA_NAME)
    .replace(/ฮีลใจ/g, ASTRIA_NAME)
    .replace(/ฮีลเจย์/g, ASTRIA_NAME);
};

/** Lowercase slug for filenames, e.g. "healjai-love-reading.png". */
export const getBrandSlug = (origin?: string | null): string =>
  isHealJaiBrand(origin) ? HEALJAI_SLUG : ASTRIA_SLUG;

/** Public site name used in shared/exported artwork. */
export const getBrandSite = (origin?: string | null): string =>
  isHealJaiBrand(origin) ? HEALJAI_SITE : ASTRIA_SITE;
